import React from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import { MapPin, Clock, Shield, Truck, Gamepad2, Check, Mail, Zap, Phone, Star } from '../lib/icons';
import { getAreaBySlug } from '../data/service-areas';
import SEOHead from '../components/SEOHead';

const SERVICES = [
  {
    icon: Gamepad2,
    title: 'TMR Hall-Effect Joystick Upgrade',
    description: 'Permanently eliminates stick drift. No more phantom inputs, no calibration, no drift — ever.',
    price: 'From $60',
    color: '#39FF14',
    link: '/blog/tmr-hall-effect-joysticks-ps5',
  },
  {
    icon: Zap,
    title: 'Mouse Click Triggers & Bumpers',
    description: 'Reduces trigger travel by ~70%. Faster ADS, faster shots, consistent feel on every press.',
    price: '+$60',
    color: '#00BFFF',
    link: '/blog/ps5-mouse-click-triggers',
  },
  {
    icon: Gamepad2,
    title: 'Clicky Face Buttons & D-Pad',
    description: 'Mechanical switch feel on X, O, □, △, and D-Pad. Every input precise and satisfying.',
    price: '+$50',
    color: '#FF1744',
    link: '/custom-controllers',
  },
  {
    icon: Shield,
    title: 'Back Paddles & Back Buttons',
    description: 'Keep your thumbs on the sticks while jumping, reloading, or crouching.',
    price: 'From +$80',
    color: '#B8860B',
    link: '/blog/ps5-back-paddles-vs-back-buttons',
  },
  {
    icon: Gamepad2,
    title: 'Custom Shell & Cosmetics',
    description: 'Full cosmetic rebuild: shell color, button kit, touchpad, trim rings, and finish.',
    price: 'From +$50',
    color: '#E91E63',
    link: '/custom-controllers',
  },
  {
    icon: Zap,
    title: 'Rumble Motor Delete',
    description: 'Remove both vibration motors for a lighter, snappier competitive controller.',
    price: '+$25',
    color: '#FF9800',
    link: '/custom-controllers',
  },
];

const LocalServicePage = () => {
  const { city } = useParams<{ city: string }>();
  const area = city ? getAreaBySlug(city) : undefined;

  if (!area) return <Navigate to="/" replace />;

  const localBizSchema = {
    '@context': 'https://schema.org',
    '@type': 'LocalBusiness',
    name: 'GameFix Solutions',
    description: `PS5 controller repair, custom DualSense builds, and TMR joystick upgrades serving ${area.city}, ${area.state} and surrounding areas.`,
    telephone: '+19512168534',
    email: 'info@gamefixsolutions.com',
    url: 'https://gamefixsolutions.com',
    areaServed: [
      area.city,
      ...area.nearbyCities,
    ].map(c => ({ '@type': 'City', 'name': c })),
    serviceArea: {
      '@type': 'GeoCircle',
      geoMidpoint: { '@type': 'GeoCoordinates', latitude: area.lat, longitude: area.lng },
      geoRadius: '40000',
    },
    hasOfferCatalog: {
      '@type': 'OfferCatalog',
      name: 'PS5 Controller Services',
      itemListElement: [
        { '@type': 'Offer', name: 'TMR Joystick Upgrade (Standard)', price: '60', priceCurrency: 'USD' },
        { '@type': 'Offer', name: 'Custom PS5 DualSense Build', price: '150', priceCurrency: 'USD' },
        { '@type': 'Offer', name: 'Mouse Click Trigger Upgrade', price: '60', priceCurrency: 'USD' },
      ],
    },
    sameAs: ['https://gamefixsolutions.com'],
  };

  return (
    <div className="bg-[#0D0D0D] min-h-screen">
      <SEOHead
        title={area.metaTitle}
        description={area.metaDescription}
        keywords={area.keywords.join(', ')}
        canonical={`https://gamefixsolutions.com/${area.slug}`}
        jsonLD={localBizSchema}
      />

      {/* Hero */}
      <section className="relative bg-gradient-to-b from-black via-[#0D0D0D] to-[#0D0D0D] py-20 overflow-hidden border-b border-gray-900">
        <div className="absolute inset-0 opacity-10 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxwYXRoIGQ9Ik0zNiAxOGMzLjMxNCAwIDYgMi42ODYgNiA2cy0yLjY4NiA2LTYgNi02LTIuNjg2LTYtNiAyLjY4Ni02IDYtNiIgc3Ryb2tlPSIjMzlGRjE0IiBzdHJva2Utd2lkdGg9IjAuNSIvPjwvZz48L3N2Zz4=')]" />
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="flex items-center gap-2 mb-6">
            <MapPin className="h-4 w-4 text-[#39FF14]" />
            <span className="text-[#39FF14] text-sm font-semibold">{area.city}, {area.state} · {area.county}</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-[#E0E0E0] mb-5 leading-tight">
            {area.heroHeading}
          </h1>
          <p className="text-xl text-[#B0B0B0] mb-10 max-w-3xl leading-relaxed">
            {area.heroSubheading}
          </p>
          <div className="flex flex-col sm:flex-row gap-4">
            <Link
              to="/custom-controllers"
              className="inline-flex items-center justify-center gap-2 bg-[#39FF14] hover:bg-[#00BFFF] text-black font-bold px-7 py-3.5 rounded-lg transition-all text-base"
            >
              <Gamepad2 className="h-5 w-5" /> Build My Controller
            </Link>
            <Link
              to="/mail-in-repairs"
              className="inline-flex items-center justify-center gap-2 bg-gray-800 hover:bg-gray-700 text-[#E0E0E0] font-semibold px-7 py-3.5 rounded-lg border border-gray-700 hover:border-[#39FF14] transition-all text-base"
            >
              <Truck className="h-5 w-5" /> Mail-In Service
            </Link>
            {area.googleBusinessUrl && (
              <a
                href={area.googleBusinessUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 bg-gray-800 hover:bg-gray-700 text-[#E0E0E0] font-semibold px-7 py-3.5 rounded-lg border border-gray-700 hover:border-[#FFD700] transition-all text-base"
              >
                <Star className="h-5 w-5 text-[#FFD700]" /> Google Reviews
              </a>
            )}
          </div>
        </div>
      </section>

      {/* Local context strip */}
      <section className="bg-gray-900/60 border-b border-gray-800 py-6">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <div className="flex items-start gap-3">
              <MapPin className="h-5 w-5 text-[#39FF14] shrink-0 mt-0.5" />
              <div>
                <p className="text-xs font-semibold text-[#B0B0B0] uppercase tracking-wide">Pickup</p>
                <p className="text-[#E0E0E0] text-sm font-medium">Temecula by appt.</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Clock className="h-5 w-5 text-[#00BFFF] shrink-0 mt-0.5" />
              <div>
                <p className="text-xs font-semibold text-[#B0B0B0] uppercase tracking-wide">Drive Time</p>
                <p className="text-[#E0E0E0] text-sm font-medium">~{area.driveMinutes} min from {area.city}</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Shield className="h-5 w-5 text-[#B8860B] shrink-0 mt-0.5" />
              <div>
                <p className="text-xs font-semibold text-[#B0B0B0] uppercase tracking-wide">Warranty</p>
                <p className="text-[#E0E0E0] text-sm font-medium">90-day guarantee</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Truck className="h-5 w-5 text-[#FF1744] shrink-0 mt-0.5" />
              <div>
                <p className="text-xs font-semibold text-[#B0B0B0] uppercase tracking-wide">Mail-In</p>
                <p className="text-[#E0E0E0] text-sm font-medium">Nationwide available</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Local intro */}
      <section className="py-16 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          <div>
            <h2 className="text-3xl font-bold text-[#E0E0E0] mb-5">
              Serving {area.city} Gamers
            </h2>
            <p className="text-[#B0B0B0] leading-relaxed mb-5 text-[1.05rem]">
              {area.localIntro}
            </p>
            <div className="bg-gray-900 border border-[#39FF14]/30 rounded-xl p-5 text-sm">
              <p className="text-[#39FF14] font-semibold mb-1">Local Pickup Option</p>
              <p className="text-[#B0B0B0]">{area.proximityNote}</p>
            </div>
          </div>

          <div className="space-y-3">
            <h3 className="text-lg font-bold text-[#E0E0E0] mb-4">Why {area.city} Players Choose Us</h3>
            {[
              'Same-week turnaround — not 2–3 weeks like manufacturer repairs',
              'TMR hall-effect joysticks that permanently fix stick drift',
              'Competitive upgrades you can\'t get from Sony or retail stores',
              '90-day warranty on every upgrade we perform',
              'Nationwide mail-in service available',
              'Local pickup available by appointment in Temecula',
              'Transparent pricing — you see the quote before we start',
            ].map((item, i) => (
              <div key={i} className="flex items-start gap-3">
                <Check className="h-4 w-4 text-[#39FF14] shrink-0 mt-0.5" />
                <span className="text-[#B0B0B0] text-sm">{item}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="py-16 bg-gradient-to-b from-black to-[#0D0D0D]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-[#E0E0E0] mb-3">
              Available Services for {area.city} Customers
            </h2>
            <p className="text-[#B0B0B0] text-lg">
              Local pickup or nationwide mail-in — your choice
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {SERVICES.map((service, i) => {
              const Icon = service.icon;
              return (
                <Link
                  key={i}
                  to={service.link}
                  className="bg-gray-900 rounded-2xl p-6 border border-gray-800 hover:border-gray-600 transition-all group"
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="p-2 rounded-lg" style={{ backgroundColor: `${service.color}18` }}>
                      <Icon className="h-5 w-5" style={{ color: service.color }} />
                    </div>
                    <span className="text-[#39FF14] text-sm font-bold">{service.price}</span>
                  </div>
                  <h3 className="text-[#E0E0E0] font-bold mb-2 group-hover:text-[#39FF14] transition-colors">
                    {service.title}
                  </h3>
                  <p className="text-[#B0B0B0] text-sm leading-relaxed">{service.description}</p>
                </Link>
              );
            })}
          </div>

          <div className="mt-10 text-center">
            <Link
              to="/custom-controllers"
              className="inline-flex items-center gap-2 bg-[#39FF14] hover:bg-[#00BFFF] text-black font-bold px-8 py-4 rounded-lg transition-all text-lg"
            >
              <Gamepad2 className="h-5 w-5" /> See Full Pricing & Build Options
            </Link>
          </div>
        </div>
      </section>

      {/* How to get service */}
      <section className="py-16 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 className="text-3xl font-bold text-[#E0E0E0] mb-10 text-center">
          Two Ways to Get Your Controller Upgraded
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

          {/* Local option */}
          <div className="bg-gray-900 rounded-2xl border border-gray-800 p-7">
            <div className="flex items-center gap-3 mb-4">
              <div className="bg-[#39FF14]/10 p-2.5 rounded-xl">
                <MapPin className="h-6 w-6 text-[#39FF14]" />
              </div>
              <div>
                <p className="text-[#E0E0E0] font-bold text-lg">Local Pickup</p>
                <p className="text-[#B0B0B0] text-sm">~{area.driveMinutes} min from {area.city}</p>
              </div>
            </div>
            <ol className="space-y-3 mb-6">
              {[
                'Contact us to discuss your upgrade and get a quote',
                'Schedule a drop-off appointment in Temecula',
                'We upgrade and test your controller',
                'Pick it up — usually within 2–4 business days',
              ].map((step, i) => (
                <li key={i} className="flex items-start gap-3 text-sm text-[#B0B0B0]">
                  <span className="shrink-0 w-5 h-5 rounded-full bg-[#39FF14]/20 text-[#39FF14] text-xs font-bold flex items-center justify-center mt-0.5">
                    {i + 1}
                  </span>
                  {step}
                </li>
              ))}
            </ol>
            <a
              href="mailto:info@gamefixsolutions.com?subject=Local Pickup Appointment"
              className="w-full inline-flex items-center justify-center gap-2 bg-[#39FF14] hover:bg-[#00BFFF] text-black font-bold py-3 rounded-lg transition-all text-sm"
            >
              <Mail className="h-4 w-4" /> Schedule Appointment
            </a>
          </div>

          {/* Mail-in option */}
          <div className="bg-gray-900 rounded-2xl border border-gray-800 p-7">
            <div className="flex items-center gap-3 mb-4">
              <div className="bg-[#00BFFF]/10 p-2.5 rounded-xl">
                <Truck className="h-6 w-6 text-[#00BFFF]" />
              </div>
              <div>
                <p className="text-[#E0E0E0] font-bold text-lg">Nationwide Mail-In</p>
                <p className="text-[#B0B0B0] text-sm">Return shipping included</p>
              </div>
            </div>
            <ol className="space-y-3 mb-6">
              {[
                'Choose your upgrades and confirm a quote with us',
                'Ship your controller in a padded box or bubble mailer',
                'We upgrade it and run a full function test',
                'Receive it back in 2–4 business days with 90-day warranty',
              ].map((step, i) => (
                <li key={i} className="flex items-start gap-3 text-sm text-[#B0B0B0]">
                  <span className="shrink-0 w-5 h-5 rounded-full bg-[#00BFFF]/20 text-[#00BFFF] text-xs font-bold flex items-center justify-center mt-0.5">
                    {i + 1}
                  </span>
                  {step}
                </li>
              ))}
            </ol>
            <Link
              to="/mail-in-repairs"
              className="w-full inline-flex items-center justify-center gap-2 bg-[#00BFFF] hover:bg-[#39FF14] text-black font-bold py-3 rounded-lg transition-all text-sm"
            >
              <Truck className="h-4 w-4" /> Start Mail-In Upgrade
            </Link>
          </div>
        </div>
      </section>

      {/* Nearby cities */}
      <section className="py-12 bg-black border-t border-gray-900">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-sm font-semibold text-[#B0B0B0] uppercase tracking-widest mb-5">
            Also Serving Nearby Communities
          </p>
          <div className="flex flex-wrap gap-3">
            {area.nearbyCities.map(c => {
              const slug = c.toLowerCase().replace(/\s+/g, '-');
              return (
                <Link
                  key={c}
                  to={`/${slug}`}
                  className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-gray-900 border border-gray-800 hover:border-[#39FF14]/50 text-[#B0B0B0] hover:text-[#39FF14] text-sm font-medium transition-all"
                >
                  <MapPin className="h-3.5 w-3.5" /> {c}, CA
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="py-16 bg-gradient-to-b from-[#0D0D0D] to-black">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Gamepad2 className="h-10 w-10 text-[#39FF14] mx-auto mb-4" />
          <h2 className="text-3xl font-bold text-[#E0E0E0] mb-3">
            Ready to Upgrade Your PS5 Controller?
          </h2>
          <p className="text-[#B0B0B0] mb-8">
            Built for {area.city} players. Same-week turnaround. 90-day warranty.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/custom-controllers"
              className="inline-flex items-center justify-center gap-2 bg-[#39FF14] hover:bg-[#00BFFF] text-black font-bold px-8 py-3.5 rounded-lg transition-all"
            >
              <Gamepad2 className="h-5 w-5" /> Build My Controller
            </Link>
            <a
              href="sms:+19512168534"
              className="inline-flex items-center justify-center gap-2 bg-gray-800 hover:bg-gray-700 text-[#E0E0E0] font-semibold px-8 py-3.5 rounded-lg border border-gray-700 hover:border-[#39FF14] transition-all"
            >
              <Phone className="h-5 w-5" /> Text Us
            </a>
          </div>
          <p className="text-[#B0B0B0] text-sm mt-4">
            <a href="mailto:info@gamefixsolutions.com" className="hover:text-[#39FF14] transition-colors">
              info@gamefixsolutions.com
            </a>
            {' · '}
            <a href="sms:+19512168534" className="hover:text-[#39FF14] transition-colors">
              (951) 216-8534
            </a>
          </p>
          {area.googleBusinessUrl && (
            <a
              href={area.googleBusinessUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-[#FFD700] hover:text-yellow-300 text-sm font-medium mt-4 transition-colors"
            >
              <Star className="h-4 w-4" /> Leave us a Google Review — it helps local customers find us
            </a>
          )}
        </div>
      </section>
    </div>
  );
};

export default LocalServicePage;
